import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API } from "@/App";
import { 
  School, 
  Building2, 
  Mail, 
  Lock, 
  Phone, 
  MapPin,
  ChevronRight, 
  ArrowLeft, 
  CheckCircle,
  User,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import SharedHeader from "@/components/SharedHeader";
import { toast } from "sonner";

const INDIAN_STATES = [
  { code: "AN", name: "Andaman and Nicobar Islands" },
  { code: "AP", name: "Andhra Pradesh" },
  { code: "AR", name: "Arunachal Pradesh" },
  { code: "AS", name: "Assam" },
  { code: "BR", name: "Bihar" },
  { code: "CH", name: "Chandigarh" },
  { code: "CT", name: "Chhattisgarh" },
  { code: "DL", name: "Delhi" },
  { code: "GA", name: "Goa" },
  { code: "GJ", name: "Gujarat" },
  { code: "HR", name: "Haryana" },
  { code: "HP", name: "Himachal Pradesh" },
  { code: "JK", name: "Jammu and Kashmir" },
  { code: "JH", name: "Jharkhand" },
  { code: "KA", name: "Karnataka" },
  { code: "KL", name: "Kerala" },
  { code: "LA", name: "Ladakh" },
  { code: "MP", name: "Madhya Pradesh" },
  { code: "MH", name: "Maharashtra" },
  { code: "MN", name: "Manipur" },
  { code: "ML", name: "Meghalaya" },
  { code: "MZ", name: "Mizoram" },
  { code: "NL", name: "Nagaland" },
  { code: "OR", name: "Odisha" },
  { code: "PB", name: "Punjab" },
  { code: "PY", name: "Puducherry" },
  { code: "RJ", name: "Rajasthan" },
  { code: "SK", name: "Sikkim" },
  { code: "TN", name: "Tamil Nadu" },
  { code: "TG", name: "Telangana" },
  { code: "TR", name: "Tripura" },
  { code: "UP", name: "Uttar Pradesh" },
  { code: "UK", name: "Uttarakhand" },
  { code: "WB", name: "West Bengal" }
];

export default function SchoolRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: School Info, 2: Admin Info, 3: Success
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // School info
    name: "",
    board: "",
    school_type: "private",
    state: "",
    city: "",
    pincode: "",
    address: "",
    official_email: "",
    phone: "",
    website: "",
    grades_offered: [5, 6, 7, 8],
    
    // Admin info
    admin_name: "",
    admin_email: "",
    admin_phone: "",
    admin_designation: "",
    password: "",
    confirm_password: "",
    
    // Consent
    terms_accepted: false
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleGrade = (grade) => {
    setFormData(prev => ({
      ...prev,
      grades_offered: prev.grades_offered.includes(grade)
        ? prev.grades_offered.filter(g => g !== grade)
        : [...prev.grades_offered, grade].sort((a, b) => a - b)
    }));
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.board || !formData.state || !formData.city || !formData.official_email) {
      toast.error("Please fill all required fields");
      return false;
    }
    if (formData.grades_offered.length === 0) {
      toast.error("Please select at least one grade");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.admin_name || !formData.admin_email || !formData.password) {
      toast.error("Please fill all required fields");
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return false;
    }
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return false;
    }
    if (!formData.terms_accepted) {
      toast.error("Please accept the terms and conditions");
      return false;
    }
    // Admin email can be from any domain (not restricted to school domain)
    // Email is frozen after creation - only product admin can change it
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/school/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          board: formData.board,
          school_type: formData.school_type,
          grades_offered: formData.grades_offered,
          medium: "english",
          official_email_domain: formData.official_email.split("@")[1],
          address: {
            line1: formData.address || `${formData.name}`,
            city: formData.city,
            state: INDIAN_STATES.find(s => s.code === formData.state)?.name || formData.state,
            pincode: formData.pincode || "110001",
            country: "India"
          },
          contact: {
            principal_name: formData.admin_name,
            principal_email: formData.admin_email,
            principal_phone: formData.phone || "+91 1234567890",
            admin_name: formData.admin_name,
            admin_email: formData.admin_email,
            admin_phone: formData.admin_phone || formData.phone || "+91 1234567890"
          }
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setRegistrationResult(data);
        setStep(3);
        toast.success("School registered successfully!");
      } else {
        toast.error(data.detail?.[0]?.msg || data.detail || data.error || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100">
        <SharedHeader transparent={true} />
      </header>

      {/* Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
      </div>

      <div className="flex-1 py-8 px-4">
        <div className="relative max-w-2xl mx-auto">

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? "text-emerald-600" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 1 ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {step > 1 ? <CheckCircle className="w-5 h-5" /> : "1"}
              </div>
              <span className="text-sm font-medium hidden sm:inline">School Info</span>
            </div>
            <div className={`w-12 h-0.5 ${step >= 2 ? "bg-emerald-600" : "bg-slate-200"}`}></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? "text-emerald-600" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 2 ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {step > 2 ? <CheckCircle className="w-5 h-5" /> : "2"}
              </div>
              <span className="text-sm font-medium hidden sm:inline">Admin Setup</span>
            </div>
            <div className={`w-12 h-0.5 ${step >= 3 ? "bg-emerald-600" : "bg-slate-200"}`}></div>
            <div className={`flex items-center gap-2 ${step >= 3 ? "text-emerald-600" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 3 ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {step >= 3 ? <CheckCircle className="w-5 h-5" /> : "3"}
              </div>
              <span className="text-sm font-medium hidden sm:inline">Complete</span>
            </div>
          </div>
        </div>

        <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
              <School className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              {step === 1 && "Register Your School"}
              {step === 2 && "Setup Admin Account"}
              {step === 3 && "Registration Complete!"}
            </CardTitle>
            <CardDescription>
              {step === 1 && "Tell us about your school"}
              {step === 2 && "Create your school admin account"}
              {step === 3 && "Your school has been registered"}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-4">
            {/* Step 1: School Information */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>School Name *</Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={formData.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      placeholder=""
                      className="pl-10"
                      data-testid="school-name-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Education Board *</Label>
                    <Select value={formData.board} onValueChange={(v) => updateField("board", v)}>
                      <SelectTrigger className="mt-1" data-testid="board-select">
                        <SelectValue placeholder="" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CBSE">CBSE</SelectItem>
                        <SelectItem value="ICSE">ICSE</SelectItem>
                        <SelectItem value="STATE">State Board</SelectItem>
                        <SelectItem value="IB">IB</SelectItem>
                        <SelectItem value="IGCSE">IGCSE</SelectItem>
                        <SelectItem value="NIOS">NIOS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>School Type</Label>
                    <Select value={formData.school_type} onValueChange={(v) => updateField("school_type", v)}>
                      <SelectTrigger className="mt-1" data-testid="school-type-select">
                        <SelectValue placeholder="" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="aided">Aided</SelectItem>
                        <SelectItem value="international">International</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>State *</Label>
                    <Select value={formData.state} onValueChange={(v) => updateField("state", v)}>
                      <SelectTrigger className="mt-1" data-testid="state-select">
                        <SelectValue placeholder="" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map(state => (
                          <SelectItem key={state.code} value={state.code}>{state.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>City *</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => updateField("city", e.target.value)}
                      placeholder=""
                      className="mt-1"
                      data-testid="city-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Official School Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="email"
                      value={formData.official_email}
                      onChange={(e) => updateField("official_email", e.target.value)}
                      placeholder=""
                      className="pl-10"
                      data-testid="school-email-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    OTPs for 2FA will be sent to this email
                  </p>
                </div>

                <div>
                  <Label>Phone Number</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={formData.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      placeholder=""
                      className="pl-10"
                      data-testid="school-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Grades Offered *</Label>
                  <div className="flex gap-3 mt-2">
                    {[5, 6, 7, 8].map(grade => (
                      <button
                        key={grade}
                        type="button"
                        onClick={() => toggleGrade(grade)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          formData.grades_offered.includes(grade)
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        data-testid={`grade-${grade}-btn`}
                      >
                        Grade {grade}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={() => validateStep1() && setStep(2)}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 mt-4"
                  data-testid="continue-to-step2"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Admin Account */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-amber-700">
                    <strong>Note:</strong> Admin email address cannot be changed after registration. Contact Eduzy support if you need to update it later.
                  </p>
                </div>

                <div>
                  <Label>Admin Name *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={formData.admin_name}
                      onChange={(e) => updateField("admin_name", e.target.value)}
                      placeholder=""
                      className="pl-10"
                      data-testid="admin-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Admin Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => updateField("admin_email", e.target.value)}
                      placeholder=""
                      className="pl-10"
                      data-testid="admin-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Designation</Label>
                  <Input
                    value={formData.admin_designation}
                    onChange={(e) => updateField("admin_designation", e.target.value)}
                    placeholder=""
                    className="mt-1"
                    data-testid="admin-designation-input"
                  />
                </div>

                <div>
                  <Label>Password *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => updateField("password", e.target.value)}
                      placeholder=""
                      className="pl-10 pr-10"
                      data-testid="admin-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label>Confirm Password *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.confirm_password}
                      onChange={(e) => updateField("confirm_password", e.target.value)}
                      placeholder=""
                      className="pl-10"
                      data-testid="admin-confirm-password-input"
                    />
                  </div>
                </div>

                <div className="flex items-start gap-2 mt-4">
                  <Checkbox
                    id="terms"
                    checked={formData.terms_accepted}
                    onCheckedChange={(checked) => updateField("terms_accepted", checked)}
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-sm text-slate-600 leading-tight">
                    I agree to the{" "}
                    <Link to="/terms" className="text-emerald-600 hover:underline" target="_blank">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" className="text-emerald-600 hover:underline" target="_blank">
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                <div className="flex gap-3 mt-4">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    data-testid="submit-registration"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        Registering...
                      </span>
                    ) : (
                      "Register School"
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Success */}
            {step === 3 && registrationResult && (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    Welcome to Eduzy!
                  </h3>
                  <p className="text-slate-600">
                    Your school has been registered successfully.
                  </p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 text-left">
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">School Code</p>
                      <p className="font-mono text-lg font-bold text-emerald-600">
                        {registrationResult.school_code}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Status</p>
                      <p className="font-medium text-amber-600">
                        Pending Verification
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-700">
                    Your registration is pending verification by Eduzy team. 
                    You'll receive an email once your school is verified.
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => navigate("/school/login")}
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    data-testid="go-to-login"
                  >
                    Go to Login
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/")}
                  >
                    Back to Home
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        {step !== 3 && (
          <p className="text-center text-xs text-slate-500 mt-6">
            Need help? Contact us at{" "}
            <a href="mailto:info@contentiumtech.com" className="text-emerald-600 hover:underline">
              info@contentiumtech.com
            </a>
          </p>
        )}
        </div>
      </div>
    </div>
  );
}
